import React, { useMemo, useState } from 'react';
import { IconButton, TextField } from '@mui/material';
import { CloseRounded } from '@mui/icons-material/';
import AppTitle from '@/components/common/title';
import s from './styles.module.scss';
import { ReadSurah } from '@/infrastructure/class/read-surah';
import Link from 'next/link';
import { routes } from '@/infrastructure/const/routes';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { ayatId } from '@/components/ayas/list/item';
import { scrollToElement } from '@/utils/helpers/scroll-to-element';

interface IReadSurahSelectorFormProps {
  currentSurah: ReadSurah;
  surahs: ReadSurah[];
  ayasCount: number;
  onClose?: () => void;
}

const ReadSurahSelectorForm: React.FC<IReadSurahSelectorFormProps> = (
  props
) => {
  const { currentSurah, surahs, ayasCount, onClose = () => {} } = props;

  const [surahSearch, setSurahSearch] = useState<string>('');
  const [ayasSearch, setAyasSearch] = useState<string>('');

  const filteredSurah = useMemo<ReadSurah[]>(() => {
    if (!surahSearch.trim()) return surahs;
    return surahs.filter((surah) => {
      const { titleEn, titleAr, transliteration } = surah;
      if (
        titleEn.toLowerCase().includes(surahSearch) ||
        titleAr.toLowerCase().includes(surahSearch) ||
        transliteration.toLowerCase().includes(surahSearch)
      ) {
        return true;
      }
      return false;
    });
  }, [surahSearch, surahs]);

  const filteredAyas = useMemo<number[]>(() => {
    const ayas = Array.from(Array(ayasCount).keys());
    if (ayasSearch.trim()) ayas;
    return ayas.filter((ayatNumber) => {
      return String(ayatNumber + 1).includes(ayasSearch);
    });
  }, [ayasCount, ayasSearch]);

  const { t: tButtons } = useTranslation('buttons');

  const ayasListItemClassNames = classNames(s['item'], s['ayat']);

  const linkClassNames = (surah: ReadSurah) =>
    classNames(s['item'], {
      [s['active']]: currentSurah.slug === surah.slug,
    });

  const scrollToAyat = (number: number) => {
    const ayat = document.getElementById(ayatId(Number(number)));
    if (ayat) {
      scrollToElement(ayat, 500);
      onClose();
    }
  };

  return (
    <div className={s['form']}>
      <div className={s['header']}>
        <AppTitle className={s['title']} title={currentSurah.titleEn} />
        <IconButton onClick={onClose}>
          <CloseRounded />
        </IconButton>
      </div>
      <div className={s['body']}>
        <div className={s['surahs']}>
          <TextField
            variant="standard"
            size="small"
            placeholder={tButtons('searchSurahs')}
            onChange={(e) => setSurahSearch(e.target.value)}
          />
          <ul className={s['list']}>
            {filteredSurah.map((surah) => (
              <li key={surah.index}>
                <Link
                  className={linkClassNames(surah)}
                  href={`${routes.surahRead}/${surah.slug}`}
                  title={`${surah.index} ${surah.titleEn} ${surah.transliteration}`}
                  onClick={onClose}
                >
                  {`${surah.index} ${surah.titleEn} ${surah.transliteration}`}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className={s['ayas']}>
          <TextField
            type="number"
            variant="standard"
            size="small"
            placeholder={tButtons('ayas')}
            onChange={(e) => setAyasSearch(e.target.value)}
          />
          <ul className={s['list']}>
            {filteredAyas.map((ayatNumber) => (
              <li
                key={ayatNumber}
                className={ayasListItemClassNames}
                onClick={() => scrollToAyat(ayatNumber + 1)}
              >
                {ayatNumber + 1}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ReadSurahSelectorForm;
